import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import {
  PageHeader,
  Card,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed } from "@/lib/format";
import { printTrialBalance } from "@/lib/trialBalancePrint";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const defaultFromDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function TrialBalanceReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(today);
  const [schedule, setSchedule] = useState("all");

  const { data: scheduleData } = useQuery({
    queryKey: ["account-schedules", branchId, "trial-balance"],
    queryFn: async () => {
      const { data: res } = await api.get("/account-schedules", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const schedules = scheduleData?.schedules ?? [];

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["trial-balance", branchId, fromDate, toDate, schedule],
    queryFn: async () => {
      const { data: res } = await api.get("/trial-balance", {
        params: {
          ...reportParams,
          from_date: fromDate,
          to_date: toDate,
          schedule: schedule === "all" ? undefined : schedule,
        },
      });
      return res;
    },
    enabled: Boolean(canQuery && fromDate && toDate),
  });

  const totals = report?.totals ?? {};

  return (
    <ReportPageWrap>
      <PageHeader
        title="Trial Balance"
        subtitle="All accounts with opening, period movement and closing debit/credit columns"
        actions={
          <ReportExportButtons
            disabled={!report}
            title="Trial Balance"
            filename="trial-balance"
            printTestId="tb-print"
            onPrint={() => printTrialBalance(report, "Trial Balance", { branchId, allBranches })}
          />
        }
      />

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-5 gap-y-3">
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">From Date</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9"
              data-testid="tb-from"
            />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">To Date</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9"
              data-testid="tb-to"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[12px] text-slate-600 mb-1 block">Schedule Filter</Label>
            <Select value={schedule} onValueChange={setSchedule}>
              <SelectTrigger className="h-9" data-testid="tb-schedule">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All schedules</SelectItem>
                {schedules.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {report && (
        <ReportKpiStrip
          items={[
            { label: "Accounts", value: report.account_count },
            {
              label: "Closing Debit",
              value: <span data-testid="tb-total-debit">{aed(totals.closing_debit)}</span>,
            },
            {
              label: "Closing Credit",
              value: <span data-testid="tb-total-credit">{aed(totals.closing_credit)}</span>,
            },
            {
              label: "Status",
              value: (
                <span data-testid="tb-status">
                  {report.is_balanced ? "Balanced" : "Out of balance"}
                </span>
              ),
              emphasize: true,
            },
          ]}
        />
      )}

      <ReportTableCard
        title="Trial Balance Rows"
        actions={
          (isLoading || isFetching) ? (
            <span className="text-[12px] text-slate-500 flex items-center gap-1.5">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </span>
          ) : null
        }
      >
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">Account</th>
              <th className="text-left">Schedule</th>
              <th className="text-right">Op. Dr</th>
              <th className="text-right">Op. Cr</th>
              <th className="text-right">Per. Dr</th>
              <th className="text-right">Per. Cr</th>
              <th className="text-right">Cls. Dr</th>
              <th className="text-right">Cls. Cr</th>
            </tr>
          </thead>
          <tbody>
            {!report && !isLoading && (
              <tr>
                <td className="text-center text-slate-500 py-10" colSpan={9}>
                  Set a date range to generate the trial balance.
                </td>
              </tr>
            )}
            {report?.rows?.length === 0 && (
              <tr>
                <td className="text-center text-slate-500 py-8" colSpan={9}>
                  No account activity in this period.
                </td>
              </tr>
            )}
            {report?.rows?.map((r, i) => (
              <tr
                key={r.account_id}
                className="cursor-pointer"
                data-testid={`tb-row-${i}`}
                onClick={() => navigate(`/accounts/ledger?account_id=${r.account_id}&from_date=${fromDate}&to_date=${toDate}`)}
              >
                <td className="font-mono text-blue-700">{r.code}</td>
                <td className="text-slate-800">{r.name}</td>
                <td className="text-slate-600">{r.schedule || "—"}</td>
                <td className="text-right font-mono">{r.opening_debit > 0 ? aed(r.opening_debit) : ""}</td>
                <td className="text-right font-mono">{r.opening_credit > 0 ? aed(r.opening_credit) : ""}</td>
                <td className="text-right font-mono">{r.period_debit > 0 ? aed(r.period_debit) : ""}</td>
                <td className="text-right font-mono">{r.period_credit > 0 ? aed(r.period_credit) : ""}</td>
                <td className="text-right font-mono font-semibold">{r.closing_debit > 0 ? aed(r.closing_debit) : ""}</td>
                <td className="text-right font-mono font-semibold">{r.closing_credit > 0 ? aed(r.closing_credit) : ""}</td>
              </tr>
            ))}
          </tbody>
          {report && (
            <tfoot>
              <tr className="bg-slate-50 border-t font-semibold">
                <td className="px-3 py-2" colSpan={3}>Totals</td>
                <td className="text-right font-mono px-3 py-2">{aed(totals.opening_debit)}</td>
                <td className="text-right font-mono px-3 py-2">{aed(totals.opening_credit)}</td>
                <td className="text-right font-mono px-3 py-2">{aed(totals.period_debit)}</td>
                <td className="text-right font-mono px-3 py-2">{aed(totals.period_credit)}</td>
                <td className="text-right font-mono text-emerald-700 px-3 py-2">{aed(totals.closing_debit)}</td>
                <td className="text-right font-mono text-emerald-700 px-3 py-2">{aed(totals.closing_credit)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </ReportTableCard>
    </ReportPageWrap>
  );
}
